import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePublicConfig } from "../lib/useConfig";
import { formatPrice, formatAmount, launchActive, fullRegularPrice, detectMarket, setMarket, currencyForMarket, detectedMarketHint, dismissMarketHint } from "../lib/currency";
import { MarketSelector } from "../components/MarketSelector";
import { Check, ArrowRight, ChevronDown, Mail, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const PageHead = ({ eyebrow, title, sub }) => (
  <section className="bg-sand/60 border-b border-line">
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16">
      <span className="text-clay text-xs font-semibold tracking-[0.2em] uppercase">{eyebrow}</span>
      <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-ink">{title}</h1>
      {sub && <p className="mt-5 text-ink/70 text-lg leading-relaxed max-w-2xl">{sub}</p>}
    </div>
  </section>
);

const Prose = ({ children }) => (
  <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 prose-fl">{children}</div>
);

const H2 = ({ children }) => <h2 className="text-2xl font-bold text-ink mt-10 mb-3 font-display">{children}</h2>;
const P = ({ children }) => <p className="text-ink/75 leading-relaxed mb-4">{children}</p>;

export function HowItWorks() {
  const config = usePublicConfig();
  const steps = [
    { t: "Tell us about your organization", d: "Your mission, programs, budget, staffing, and how you raise money now. This is what lets the report speak to your actual situation." },
    { t: "Answer a few honest questions", d: "A guided set of questions across five areas. About 12–15 minutes, and it saves as you go, so you can stop and come back." },
    { t: "See where you stand", d: "Your overall score and what it means, plus a first look at how each area is doing." },
    { t: "Get the full report", d: "A detailed read with your strengths, what needs attention, the funding paths that fit, a draft funding story, and a 30/60/90-day plan." },
  ];
  return (
    <div>
      <PageHead eyebrow="How it works" title="From a few honest answers to a real plan"
        sub="A clear, straightforward look at your fundraising — candid but on your side, and built for organizations without a full-time development team." />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 space-y-6">
        {steps.map((s, i) => (
          <div key={s.t} className="flex gap-6 p-7 bg-white border border-line rounded-md hover-lift">
            <div className="font-display text-3xl font-extrabold text-gold shrink-0 w-12">{i + 1}</div>
            <div>
              <h3 className="font-display font-semibold text-navy text-xl">{s.t}</h3>
              <p className="mt-2 text-charcoal/75 leading-relaxed">{s.d}</p>
            </div>
          </div>
        ))}
        <div className="mt-8 grid sm:grid-cols-5 gap-3">
          {(config?.categories || []).map((c) => (
            <div key={c.key} className="p-4 bg-ivory border border-line rounded-[3px] text-center">
              <p className="font-display font-semibold text-navy text-sm">{c.name}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 p-6 bg-sand/50 border border-line rounded-md">
          <p className="text-sm text-ink/70 leading-relaxed">
            <span className="font-semibold text-ink">A note on how it works:</span> we pair a structured fundraising
            framework with technology that helps read your answers and any documents you share. Your report comes from
            what you tell us — it can't account for things you leave out, and it won't pretend to know how a specific
            funder will decide.
          </p>
        </div>
        <div className="text-center pt-6">
          <Link to="/assessment/new" className="btn-gold inline-flex items-center gap-2 font-semibold px-8 py-4 rounded-[3px]">
            Check Your Funding Readiness <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
}

const COMPARE_ROWS = [
  { label: "Guided readiness assessment", free: "Short check", focus: "Full", full: "Full" },
  { label: "Preliminary overall score", free: true, focus: true, full: true },
  { label: "One strength, gap & next step", free: true, focus: true, full: true },
  { label: "Scores across all five areas", free: false, focus: true, full: true },
  { label: "Your strongest funding assets", free: false, focus: true, full: true },
  { label: "Key areas to strengthen", free: false, focus: true, full: true },
  { label: "Top recommended funding paths", free: false, focus: true, full: true },
  { label: "Immediate next steps", free: false, focus: true, full: true },
  { label: "Downloadable Focus Report", free: false, focus: true, full: true },
  { label: "Detailed analysis specific to you", free: false, focus: false, full: true },
  { label: "Draft funding story", free: false, focus: false, full: true },
  { label: "Approaches to prioritize (and pause)", free: false, focus: false, full: true },
  { label: "30/60/90-day action plan", free: false, focus: false, full: true },
  { label: "Leadership-ready recommendations", free: false, focus: false, full: true },
  { label: "Downloadable Fundability Blueprint", free: false, focus: false, full: true },
  { label: "Saved access to your results", free: false, focus: false, full: true },
];

const Cell = ({ v, dark }) => {
  if (v === true) return <Check size={17} className={`mx-auto ${dark ? "text-ochre" : "text-forest"}`} />;
  if (v === false) return <span className={`block text-center ${dark ? "text-cream/25" : "text-ink/20"}`}>—</span>;
  return <span className={`block text-center text-xs font-medium ${dark ? "text-cream/90" : "text-ink/70"}`}>{v}</span>;
};

export function Pricing() {
  const config = usePublicConfig();
  const [market, setMarketState] = useState(detectMarket());
  const currency = currencyForMarket(market);
  const onLaunch = launchActive(config?.pricing, currency);
  const [hint, setHint] = useState(() => detectedMarketHint());
  useEffect(() => { if (hint) dismissMarketHint(); }, []); // show once ever
  const handleMarket = (code) => { setMarketState(code); setMarket(code); if (hint) setHint(null); };
  const freeFeat = ["Short funding check", "Preliminary overall score", "One key strength", "One area to strengthen", "One practical next step"];
  const focusFeat = ["Full assessment", "Scores across all five areas", "Your strongest funding assets", "Key areas to strengthen", "Top recommended funding paths", "Immediate next steps", "Downloadable Focus Report"];
  const fullFeat = ["Everything in the Focus Report", "Detailed analysis specific to your organization", "Your strongest funding paths", "What to strengthen before your next ask", "Draft funding story", "Funding approaches to prioritize — and which to pause", "30/60/90-day action plan", "Leadership-ready recommendations", "Downloadable Fundability Blueprint", "Saved access to your results"];
  return (
    <div>
      <PageHead eyebrow="Pricing" title="Simple, honest pricing" sub="Start with a free snapshot. Step up to Focus to know what to work on first, or the Blueprint for a full funding strategy and 90-day plan." />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12">
        <MarketSelector market={market} onChange={handleMarket} />
        {hint && (
          <div data-testid="market-hint" className="mt-3 mx-auto max-w-md flex items-center justify-center gap-2 text-xs text-clay bg-clay/5 border border-clay/20 rounded-full px-4 py-2">
            <span>Looks like you're in {hint.label} — showing {hint.currency.toUpperCase()}. Not right? Pick your country above.</span>
            <button onClick={() => setHint(null)} data-testid="market-hint-dismiss" aria-label="Dismiss" className="shrink-0 text-clay/50 hover:text-clay transition-colors"><X size={13} /></button>
          </div>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-3 gap-6 items-stretch">
        {/* Free */}
        <div className="p-8 bg-white border border-line rounded-md flex flex-col" data-testid="pricing-card-free">
          <h3 className="font-display font-bold text-navy text-xl">Free Funding Snapshot</h3>
          <div className="mt-3 font-display text-4xl font-extrabold text-navy" data-testid="price-free">{formatAmount(currency, 0)}</div>
          <p className="mt-1 text-sm text-muted-foreground">A quick look at where your organization stands.</p>
          <ul className="mt-6 space-y-3 text-sm text-charcoal/80 flex-1">
            {freeFeat.map((x) => <li key={x} className="flex gap-2.5"><Check size={16} className="text-forest mt-0.5 shrink-0" />{x}</li>)}
          </ul>
          <Link to="/assessment/new" data-testid="pricing-page-free"
            className="mt-8 block text-center border border-ink text-ink font-semibold px-6 py-3.5 rounded-[3px] hover:bg-ink hover:text-cream transition-colors">Get My Free Snapshot</Link>
        </div>

        {/* Focus */}
        <div className="p-8 bg-cream border-2 border-ochre/60 rounded-md flex flex-col relative" data-testid="pricing-card-focus">
          <span className="absolute -top-3 left-8 text-[11px] bg-clay text-cream font-bold px-3 py-1 rounded-full">Most Popular</span>
          <h3 className="font-display font-bold text-ink text-xl">Focus Report</h3>
          <div className="mt-3 font-display text-4xl font-extrabold text-ink" data-testid="price-focus">{formatPrice(config?.pricing, "focus", currency)}<span className="text-base font-medium text-ink/50"> one-time</span></div>
          <p className="mt-1 text-sm text-ink/60">See the bigger picture and know what to work on first.</p>
          <ul className="mt-6 space-y-3 text-sm text-ink/80 flex-1">
            {focusFeat.map((x) => <li key={x} className="flex gap-2.5"><Check size={16} className="text-clay mt-0.5 shrink-0" />{x}</li>)}
          </ul>
          <Link to="/assessment/new" data-testid="pricing-page-focus"
            className="mt-8 block text-center bg-clay text-cream font-semibold px-6 py-3.5 rounded-[3px] hover:bg-clay/90 transition-colors">Get My Focus Report</Link>
        </div>

        {/* Full */}
        <div className="p-8 bg-green text-cream rounded-md relative grain overflow-hidden flex flex-col" data-testid="pricing-card-full">
          <span className="absolute top-6 right-6 z-10 text-xs bg-ochre/90 text-ink font-semibold px-3 py-1 rounded-full">Full Strategy</span>
          <div className="relative z-10 flex flex-col flex-1">
            <h3 className="font-display font-bold text-xl">Full Fundability Blueprint</h3>
            <div className="mt-3 font-display text-4xl font-extrabold" data-testid="price-full">{formatPrice(config?.pricing, "full", currency)}</div>
            {onLaunch ? (
              <p className="mt-1 text-xs" data-testid="launch-note-full"><span className="text-ochre font-semibold uppercase tracking-wider">Launch price</span><span className="text-cream/60"> · Regularly {formatAmount(currency, fullRegularPrice(config?.pricing, currency))}</span></p>
            ) : (
              <p className="mt-1 text-xs text-cream/60">One-time</p>
            )}
            <p className="mt-2 text-sm text-cream/70">A deeper funding strategy, with clear priorities and a practical 90-day plan.</p>
            <ul className="mt-6 space-y-3 text-sm text-cream/90 flex-1">
              {fullFeat.map((x) => <li key={x} className="flex gap-2.5"><Check size={16} className="text-ochre mt-0.5 shrink-0" />{x}</li>)}
            </ul>
            <Link to="/assessment/new" data-testid="pricing-page-full"
              className="mt-8 block text-center btn-gold font-semibold px-6 py-3.5 rounded-[3px]">Get My Full Blueprint</Link>
          </div>
        </div>
      </div>

      {/* Value differentiation */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pb-4 grid sm:grid-cols-2 gap-4">
        <div className="p-5 bg-cream border border-ochre/40 rounded-md">
          <p className="text-xs uppercase tracking-wider text-clay font-semibold">Focus Report</p>
          <p className="mt-1 font-display text-lg text-ink">“What should we work on first?”</p>
        </div>
        <div className="p-5 bg-green text-cream rounded-md grain overflow-hidden relative">
          <p className="text-xs uppercase tracking-wider text-ochre font-semibold relative z-10">Full Blueprint</p>
          <p className="mt-1 font-display text-lg relative z-10">“What should our funding strategy be, and how do we move forward?”</p>
        </div>
      </div>

      {/* Comparison table */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pb-8">
        <h2 className="text-center text-2xl font-bold text-ink font-display mb-8">Compare what's included</h2>
        <div className="overflow-x-auto rounded-md border border-line bg-white">
          <table className="w-full text-sm" data-testid="pricing-compare-table">
            <thead>
              <tr className="border-b border-line bg-sand/40">
                <th className="text-left font-display font-semibold text-ink px-5 py-4 w-2/5">What you get</th>
                <th className="font-display font-semibold text-navy px-3 py-4">Free</th>
                <th className="font-display font-semibold text-clay px-3 py-4">Focus</th>
                <th className="font-display font-semibold text-forest px-3 py-4">Full</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((r, i) => (
                <tr key={r.label} className={`border-b border-line/70 ${i % 2 ? "bg-ivory/40" : ""}`}>
                  <td className="px-5 py-3 text-ink/80">{r.label}</td>
                  <td className="px-3 py-3"><Cell v={r.free} /></td>
                  <td className="px-3 py-3"><Cell v={r.focus} /></td>
                  <td className="px-3 py-3"><Cell v={r.full} /></td>
                </tr>
              ))}
              <tr className="bg-sand/40">
                <td className="px-5 py-4 font-display font-semibold text-ink">Price</td>
                <td className="px-3 py-4 text-center font-display font-bold text-navy">{formatAmount(currency, 0)}</td>
                <td className="px-3 py-4 text-center font-display font-bold text-clay">{formatPrice(config?.pricing, "focus", currency)}</td>
                <td className="px-3 py-4 text-center font-display font-bold text-forest">{formatPrice(config?.pricing, "full", currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-center text-xs text-muted-foreground">You choose to upgrade after your free snapshot — no charge until you decide. Focus buyers can upgrade to the Blueprint within 14 days and only pay the difference.</p>
      </div>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
        <div className="flex items-start gap-3 p-5 bg-white border border-line rounded-md">
          <ShieldCheck size={18} className="text-copper mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground leading-relaxed">{config?.disclaimer_short}</p>
        </div>
      </div>
    </div>
  );
}

export function About() {
  return (
    <div>
      <PageHead eyebrow="About" title="Why this exists"
        sub="FundReady Lab is a practical tool for nonprofit and community leaders. Before your next ask, it helps you look honestly at the building blocks funders pay attention to — and decide what to strengthen first." />
      <Prose>
        <P>Most fundraising advice jumps straight to tactics: find more grants, send more emails, run another event. But tactics can't fix an unclear story, thin evidence, or systems that aren't ready to handle more money.</P>
        <P>FundReady Lab was built to help you fix the building blocks first — your story, your evidence, your relationships, and the systems behind them — so your next ask stands on a stronger foundation.</P>
        <H2>What we're after</H2>
        <P>Not to hand every organization the same plan, but to help you make better funding decisions for the organization you have today — at the stage you're actually at.</P>
        <H2>An honest promise</H2>
        <P>A good score won't guarantee a yes, and a lower one doesn't mean your work matters less. What we can do is show you where you're strong, where to put your energy next, and which funding paths are worth your time.</P>
      </Prose>
    </div>
  );
}

export function FAQ() {
  const faqs = [
    { q: "What does FundReady Lab look at?", a: "Five things a funder or donor pays attention to: how clearly you tell your story, whether you can show your work makes a difference, how you bring in money, the relationships behind your asks, and whether your leadership and systems can handle more funding." },
    { q: "Will a high score guarantee that we receive funding?", a: "No. Funding decisions depend on a lot — a funder's priorities, their budget, timing, relationships, and who else is applying. Your score shows how prepared you look to pursue funding, not whether a particular funder will say yes." },
    { q: "Is this only for grant funding?", a: "No. We look at grants, individual giving, monthly donors, sponsorships, membership, earned revenue, events, partnerships, and more — and point you toward the paths that fit you best." },
    { q: "What happens if our organization is still new?", a: "That's completely fine. New organizations aren't expected to have the systems or track record of an established nonprofit. Your recommendations reflect your stage and focus on the foundations worth putting in place first." },
    { q: "Do I have to upload documents?", a: "No. Uploads are optional. They help sharpen your report, but you can finish the whole thing without sharing a single file." },
    { q: "How is my score worked out?", a: "It comes mostly from your answers, using a clear framework with set weights. Technology helps read your written responses and any documents you share — it doesn't move your score on a whim." },
    { q: "Can I take it again later?", a: "Yes. Start a fresh check anytime from your dashboard as your organization changes. It's a good way to see progress." },
    { q: "Is my information private?", a: "Yes. Your answers and any files you upload stay private and are never shown publicly. You can delete your account and everything in it whenever you want." },
    { q: "Can this replace a fundraising consultant?", a: "It's a tool to help you think clearly and prioritize — not a stand-in for hands-on help. If anything, it makes working with a consultant more productive because you'll arrive knowing what to focus on." },
    { q: "What if we leave something out?", a: "The report tells you plainly when information is missing instead of guessing or making things up, so you know exactly what to gather next." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <div>
      <PageHead eyebrow="FAQ" title="Frequently asked questions" />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 space-y-3">
        {faqs.map((f, i) => (
          <div key={f.q} className="border border-line rounded-md bg-white overflow-hidden" data-testid={`faq-item-${i}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 p-5 text-left">
              <span className="font-display font-semibold text-navy">{f.q}</span>
              <ChevronDown size={18} className={`text-gold shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <div className="px-5 pb-5 text-charcoal/75 leading-relaxed animate-fade-in">{f.a}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Contact() {
  const [sent, setSent] = useState(false);
  return (
    <div>
      <PageHead eyebrow="Contact" title="Get in touch" sub="Questions about the assessment, pricing, or your report? We'd love to hear from you." />
      <div className="max-w-xl mx-auto px-5 sm:px-8 py-16">
        {sent ? (
          <div className="p-6 bg-forest/10 border border-forest/30 text-forest rounded-md text-center" data-testid="contact-success">
            Thanks for reaching out—we'll be in touch soon.
          </div>
        ) : (
          <form onSubmit={async (e) => {
            e.preventDefault();
            const f = e.target;
            const name = f[0].value.trim(), email = f[1].value.trim(), message = f[2].value.trim();
            try {
              await api.post("/contact", { name, email, message });
              setSent(true);
              toast.success("Message sent");
            } catch (err) {
              toast.error(formatApiError(err.response?.data?.detail));
            }
          }} className="space-y-4" data-testid="contact-form">
            <input required placeholder="Your name" className="w-full px-4 py-3 bg-white border border-line rounded-[3px] focus:ring-2 focus:ring-gold outline-none" data-testid="contact-name" />
            <input required type="email" placeholder="Email" className="w-full px-4 py-3 bg-white border border-line rounded-[3px] focus:ring-2 focus:ring-gold outline-none" data-testid="contact-email" />
            <textarea required rows={5} placeholder="How can we help?" className="w-full px-4 py-3 bg-white border border-line rounded-[3px] focus:ring-2 focus:ring-gold outline-none" data-testid="contact-message" />
            <button className="btn-gold w-full font-semibold py-3.5 rounded-[3px] flex items-center justify-center gap-2" data-testid="contact-submit"><Mail size={16} /> Send message</button>
          </form>
        )}
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <div>
      <PageHead eyebrow="Legal" title="Privacy Policy" />
      <Prose>
        <P>Your privacy matters. This policy explains what we collect, how we use it, and the controls you have.</P>
        <H2>What we collect</H2>
        <P>Account details (name, email), the answers you provide in the assessment, and any documents you choose to upload. Document uploads are optional.</P>
        <H2>How uploaded documents are used</H2>
        <P>Uploaded documents are used solely to inform your Fundability Blueprint analysis. They are stored securely, are never publicly exposed, and are only accessible to you and authorized administrators. We do not sell your data.</P>
        <H2>Your controls</H2>
        <P>You can delete your account and all associated assessment data and documents at any time from your dashboard. Deletion is permanent.</P>
        <H2>Security</H2>
        <P>We use secure authentication and access controls. Reports and uploaded files are protected and require authorization to access.</P>
      </Prose>
    </div>
  );
}

export function Terms() {
  return (
    <div>
      <PageHead eyebrow="Legal" title="Terms of Use" />
      <Prose>
        <P>By using FundReady Lab, you agree to these terms.</P>
        <H2>Service description</H2>
        <P>FundReady Lab provides a strategic fundraising-readiness assessment and report. It is intended to support planning and decision-making.</P>
        <H2>No guarantee of funding</H2>
        <P>Results do not guarantee grants, donations, sponsorships, investment, or any other form of funding. You are responsible for decisions made based on the report.</P>
        <H2>Your responsibilities</H2>
        <P>You confirm that you are authorized to submit organizational information and any documents you upload, and that the information you provide is accurate to the best of your knowledge.</P>
        <H2>Payments</H2>
        <P>The Full Fundability Blueprint is a one-time purchase. Reports are generated only after payment is confirmed.</P>
      </Prose>
    </div>
  );
}

export function Disclaimer() {
  const config = usePublicConfig();
  return (
    <div>
      <PageHead eyebrow="Legal" title="Disclaimer" />
      <Prose>
        <P>{config?.disclaimer_report}</P>
        <P>{config?.disclaimer_short}</P>
        <P>The analysis and recommendations reflect information you provide and any supporting materials you submit. They are strategic guidance, not a promise of any funding outcome. Strong organizations can still be unsuccessful in individual funding applications.</P>
      </Prose>
    </div>
  );
}
